/**
 * Labels shared across the EhrSync views: status, direction, channel state, run
 * result and patient link status, keyed by the @models/ehr-sync union members.
 */
import { defineMessages } from 'react-intl';

import type {
  EhrEnvironmentKind,
  EhrPatientLinkStatus,
  EhrResourceState,
  EhrSyncDirection,
  EhrSyncRegistryState,
  EhrSyncResult,
  EhrSyncStatus,
} from '@models/ehr-sync';

export const statusMessages = defineMessages<EhrSyncStatus>({
  connected: { id: 'ehrSync.status.connected', defaultMessage: 'Connected' },
  degraded: { id: 'ehrSync.status.degraded', defaultMessage: 'Degraded' },
  failed: { id: 'ehrSync.status.failed', defaultMessage: 'Failed' },
  pending: { id: 'ehrSync.status.pending', defaultMessage: 'Pending' },
});

export const directionMessages = defineMessages<EhrSyncDirection>({
  inbound: { id: 'ehrSync.direction.inbound', defaultMessage: 'Inbound' },
  outbound: { id: 'ehrSync.direction.outbound', defaultMessage: 'Outbound' },
  bidirectional: {
    id: 'ehrSync.direction.bidirectional',
    defaultMessage: 'Two-way',
  },
});

export const resourceStateMessages = defineMessages<EhrResourceState>({
  running: { id: 'ehrSync.resourceState.running', defaultMessage: 'Running' },
  paused: { id: 'ehrSync.resourceState.paused', defaultMessage: 'Paused' },
  stopped: { id: 'ehrSync.resourceState.stopped', defaultMessage: 'Stopped' },
  stopping: {
    id: 'ehrSync.resourceState.stopping',
    defaultMessage: 'Stopping',
  },
  error: { id: 'ehrSync.resourceState.error', defaultMessage: 'Error' },
});

export const resultMessages = defineMessages<EhrSyncResult>({
  success: { id: 'ehrSync.result.success', defaultMessage: 'Success' },
  partial: { id: 'ehrSync.result.partial', defaultMessage: 'Partial' },
  failed: { id: 'ehrSync.result.failed', defaultMessage: 'Failed' },
});

export const linkStatusMessages = defineMessages<EhrPatientLinkStatus>({
  pending: { id: 'ehrSync.linkStatus.pending', defaultMessage: 'Pending' },
  needsReview: {
    id: 'ehrSync.linkStatus.needsReview',
    defaultMessage: 'Needs review',
  },
  autoLinked: {
    id: 'ehrSync.linkStatus.autoLinked',
    defaultMessage: 'Auto-linked',
  },
  linked: { id: 'ehrSync.linkStatus.linked', defaultMessage: 'Linked' },
  rejected: { id: 'ehrSync.linkStatus.rejected', defaultMessage: 'Rejected' },
  noMatch: { id: 'ehrSync.linkStatus.noMatch', defaultMessage: 'No match' },
  paused: { id: 'ehrSync.linkStatus.paused', defaultMessage: 'Paused' },
  unlinked: { id: 'ehrSync.linkStatus.unlinked', defaultMessage: 'Unlinked' },
});

/** Chip labels for registry states other than active. */
export const registryStateMessages = defineMessages<
  Exclude<EhrSyncRegistryState, 'active'>
>({
  provisioning: {
    id: 'ehrSync.registryState.provisioning',
    defaultMessage: 'Provisioning',
  },
  disabled: {
    id: 'ehrSync.registryState.disabled',
    defaultMessage: 'Disabled',
  },
  zoneNotFound: {
    id: 'ehrSync.registryState.zoneNotFound',
    defaultMessage: 'Zone not found',
  },
  zoneNameMismatch: {
    id: 'ehrSync.registryState.zoneNameMismatch',
    defaultMessage: 'Zone renamed',
  },
});

/** One-line explanations under the registry state chips. */
export const registryStateNotes = defineMessages<
  Exclude<EhrSyncRegistryState, 'active'>
>({
  provisioning: {
    id: 'ehrSync.registryState.provisioning.note',
    defaultMessage: 'Registered. No QIE channels running yet.',
  },
  disabled: {
    id: 'ehrSync.registryState.disabled.note',
    defaultMessage: 'Sync is turned off for this connection.',
  },
  zoneNotFound: {
    id: 'ehrSync.registryState.zoneNotFound.note',
    defaultMessage:
      'QIE has no zone with this name. It may have been renamed in the console.',
  },
  zoneNameMismatch: {
    id: 'ehrSync.registryState.zoneNameMismatch.note',
    defaultMessage:
      'The zone was renamed in QIE. Rename it back, or register it again under the new name.',
  },
});

export const ehrEnvironmentMessages = defineMessages<EhrEnvironmentKind>({
  sandbox: { id: 'ehrSync.ehrEnvironment.sandbox', defaultMessage: 'Sandbox' },
  nonProd: { id: 'ehrSync.ehrEnvironment.nonProd', defaultMessage: 'NonProd' },
  production: {
    id: 'ehrSync.ehrEnvironment.production',
    defaultMessage: 'Production',
  },
});

export const commonMessages = defineMessages({
  never: { id: 'ehrSync.common.never', defaultMessage: 'Never' },
  unreachableTitle: {
    id: 'ehrSync.common.unreachableTitle',
    defaultMessage: "Couldn't reach the integration service",
  },
  loadFailedTitle: {
    id: 'ehrSync.common.loadFailedTitle',
    defaultMessage: "Couldn't load EHR integrations",
  },
  retry: { id: 'ehrSync.common.retry', defaultMessage: 'Retry' },
});
